import React from 'react';
import { useTranslation } from '../utils/i18n';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  itemsPerPage?: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  itemsPerPage = 10,
  totalItems,
  onPageChange
}) => {
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(totalItems, currentPage * itemsPerPage);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      console.log("Navigating to page:", page);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
      console.log("Navigating to page:", currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
      console.log("Navigating to page:", currentPage - 1);
    }
  };

  // Function to determine if a page should be shown in the normal range
  const shouldShowPage = (page: number) => {
    // Show pages around the current page, and the first/last pages
    const delta = 1; // Number of pages to show around the current page
    return (
      page === 1 ||
      page === totalPages ||
      (page >= currentPage - delta && page <= currentPage + delta)
    );
  };

  return (
    <div className="container-banksinn-pagination">
      <div className="text-neutral-100" style={{ fontSize: '14px' }}>
        {t("component.pagination.showing")}
        {' '}{startItem} – {endItem}
        <span className="text-neutral-70"> {t("component.pagination.of")} {totalItems}</span>
      </div>
      <div className="banksinn-pagination">
        <li title="Previous Page" className="banksinn-pagination-prev">
          <button
            type="button"
            className="btn btn-neutral-secondary banksinn-pagination-item-link"
            style={{ fontSize: '12px' }}
            aria-label="Previous Page"
            onClick={prevPage}
            disabled={currentPage === 1}
          >
            <svg
              width="6"
              height="12"
              viewBox="0 0 6 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.9499 6.53261C1.8796 6.46288 1.8238 6.37993 1.78573 6.28854C1.74765 6.19714 1.72805 6.09912 1.72805 6.00011C1.72805 5.9011 1.74765 5.80307 1.78573 5.71167C1.8238 5.62028 1.8796 5.53733 1.9499 5.46761L5.3924 2.03261C5.46269 1.96288 5.51849 1.87993 5.55656 1.78854C5.59464 1.69714 5.61424 1.59912 5.61424 1.50011C5.61424 1.4011 5.59464 1.30307 5.55656 1.21167C5.51849 1.12028 5.46269 1.03733 5.3924 0.967606C5.25187 0.827918 5.06179 0.749512 4.86365 0.749512C4.66551 0.749512 4.47542 0.827918 4.3349 0.967606L0.892395 4.41011C0.471043 4.83198 0.234375 5.40385 0.234375 6.00011C0.234375 6.59636 0.471043 7.16823 0.892395 7.59011L4.3349 11.0326C4.47459 11.1712 4.66314 11.2493 4.8599 11.2501C4.9586 11.2507 5.05645 11.2318 5.14783 11.1944C5.2392 11.1571 5.32232 11.1021 5.3924 11.0326C5.46269 10.9629 5.51849 10.8799 5.55656 10.7885C5.59464 10.6971 5.61424 10.5991 5.61424 10.5001C5.61424 10.4011 5.59464 10.3031 5.55656 10.2117C5.51849 10.1203 5.46269 10.0373 5.3924 9.96761L1.9499 6.53261Z"
                fill="#595959"
              />
            </svg>
            <div className="ms-2">{t("component.pagination.prev")}</div>
          </button>
        </li>

        {Array.from({ length: totalPages }, (_, i) => {
          const pageNumber = i + 1;
          if (shouldShowPage(pageNumber)) {
            return (
              <li
                key={pageNumber}
                title={pageNumber.toString()}
                className={`banksinn-pagination-item banksinn-pagination-item-${pageNumber} ${
                  currentPage === pageNumber ? 'banksinn-pagination-item-active' : ''
                }`}
              >
                <div onClick={() => goToPage(pageNumber)}>
                  {pageNumber}
                </div>
              </li>
            );
          } else if (pageNumber === 2 && currentPage > 3) {
            return (
              <li key="jump-prev" title="Previous 5 Pages" className="banksinn-pagination-jump-prev">
                <div
                  className="banksinn-pagination-item-container"
                  onClick={() => goToPage(Math.max(1, currentPage - 5))}
                >
                  <span className="banksinn-pagination-item-ellipsis">•••</span>
                </div>
              </li>
            );
          } else if (pageNumber === totalPages - 1 && currentPage < totalPages - 2) {
            return (
              <li key="jump-next" title="Next 5 Pages" className="banksinn-pagination-jump-next">
                <div
                  className="banksinn-pagination-item-container"
                  onClick={() => goToPage(Math.min(totalPages, currentPage + 5))}
                >
                  <span className="banksinn-pagination-item-ellipsis">•••</span>
                </div>
              </li>
            );
          }
          return null;
        })}

        <li title="Next Page" className="banksinn-pagination-next">
          <button
            type="button"
            className="btn btn-neutral-secondary banksinn-pagination-item-link"
            style={{ fontSize: '12px' }}
            aria-label="Next Page"
            onClick={nextPage}
            disabled={currentPage === totalPages}
          >
            <div className="me-2">{t("component.pagination.next")}</div>
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.54997 4.41011L2.10747 0.967606C1.96695 0.827918 1.77686 0.749512 1.57872 0.749512C1.38058 0.749512 1.1905 0.827918 1.04997 0.967606C0.979677 1.03733 0.923882 1.12028 0.885805 1.21167C0.847729 1.30307 0.828125 1.4011 0.828125 1.50011C0.828125 1.59912 0.847729 1.69714 0.885805 1.78854C0.923882 1.87993 0.979677 1.96288 1.04997 2.03261L4.49997 5.46761C4.57027 5.53733 4.62607 5.62028 4.66414 5.71167C4.70222 5.80307 4.72182 5.9011 4.72182 6.00011C4.72182 6.09912 4.70222 6.19714 4.66414 6.28854C4.62607 6.37993 4.57027 6.46288 4.49997 6.53261L1.04997 9.96761C0.908746 10.1078 0.82901 10.2984 0.828307 10.4975C0.827603 10.6965 0.905991 10.8876 1.04622 11.0289C1.18646 11.1701 1.37705 11.2498 1.57607 11.2505C1.7751 11.2512 1.96625 11.1728 2.10747 11.0326L5.54997 7.59011C5.97133 7.16823 6.208 6.59636 6.208 6.00011C6.208 5.40385 5.97133 4.83198 5.54997 4.41011Z"
                fill="#595959"
              />
            </svg>
          </button>
        </li>
      </div>
    </div>
  );
};

export default Pagination;
